import './lib/i18n'; // MUST be first import — initializes i18next before React renders
import i18n from 'i18next';
import React from "react";
import "./index.css";
import ReactDOM from "react-dom/client";
import App from "./App";
import { JobCenterProvider } from "./hooks/useJobCenter";
import { SessionContextProvider } from "@supabase/auth-helpers-react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "./lib/supabaseClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./lib/queryClient.ts";
import ErrorBoundary from "./components/ErrorBoundary";
import { AuthProvider } from "./contexts/AuthContext";

// Synchronously apply the persisted language before first React paint to avoid
// a flash where the app renders in the default language then switches.
try {
  const raw = localStorage.getItem('app-language-preference');
  const lang = raw ? (JSON.parse(raw)?.state?.language ?? 'fr') : 'fr';
  if (lang === 'ar') {
    document.documentElement.dir = 'rtl';
    document.documentElement.lang = 'ar';
    i18n.changeLanguage('ar');
  }
} catch {}

const rootElement = document.getElementById("root");

if (!rootElement) {
  throw new Error("Could not find root element to mount to");
}

const root = ReactDOM.createRoot(rootElement);

let initialSession: Session | null = null;
try {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  initialSession = session;
} catch (error) {
  console.warn("Unable to hydrate Supabase session before render", error);
}

root.render(
  <React.StrictMode>
    <SessionContextProvider
      supabaseClient={supabase}
      initialSession={initialSession}
    >
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <JobCenterProvider>
            <ErrorBoundary>
              <App />
            </ErrorBoundary>
          </JobCenterProvider>
        </AuthProvider>
      </QueryClientProvider>
    </SessionContextProvider>
  </React.StrictMode>
);
